<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import paymentOnlineService, { type PaymentOnline } from '../services/payment-online.service'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()

const clubUuid = route.params.uuid as string
const paymentOnlineUuid = route.query.payment_online_uuid as string

const paymentOnline = ref<PaymentOnline | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

// Computed
const formattedAmount = computed(() => {
  if (!paymentOnline.value) return ''
  return new Intl.NumberFormat(({ pl: 'pl-PL', en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', cs: 'cs-CZ', nl: 'nl-NL', sv: 'sv-SE', da: 'da-DK', hu: 'hu-HU', ro: 'ro-RO', no: 'nb-NO', lt: 'lt-LT', hr: 'hr-HR', fi: 'fi-FI' }[locale.value] || 'en-US'), {
    style: 'currency',
    currency: paymentOnline.value.currency
  }).format(paymentOnline.value.amount)
})

// Methods
const loadPaymentStatus = async () => {
  if (!paymentOnlineUuid) {
    error.value = t('paymentCancel.noPaymentId')
    loading.value = false
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await paymentOnlineService.getPaymentOnlineStatus(clubUuid, paymentOnlineUuid)
    paymentOnline.value = response.payment_online
  } catch (err: any) {
    console.error('Error loading payment status:', err)
    error.value = err.response?.data?.message || t('paymentCancel.errorDefault')
  } finally {
    loading.value = false
  }
}

const goToClub = () => {
  router.push(`/club/${clubUuid}`)
}

const tryAgain = () => {
  // Go back to club where user can initiate payment again
  router.push(`/club/${clubUuid}`)
}

// Lifecycle
onMounted(() => {
  loadPaymentStatus()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-6">
      <!-- Loading State -->
      <div v-if="loading" class="max-w-2xl mx-auto">
        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
          <div class="flex items-center justify-center py-12">
            <div class="text-center">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p class="text-gray-600">{{ t('paymentCancel.checkingStatus') }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="max-w-2xl mx-auto">
        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
          <div class="text-center py-12">
            <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-gray-900 mb-2">{{ t('paymentCancel.errorTitle') }}</h1>
            <p class="text-gray-600 mb-6">{{ error }}</p>
            <button
              @click="goToClub"
              class="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
            >
              {{ t('paymentCancel.backToClub') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Cancel State -->
      <div v-else-if="paymentOnline" class="max-w-2xl mx-auto">
        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
          <div class="text-center py-8">
            <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-100 mb-4">
              <svg class="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-gray-900 mb-2">{{ t('paymentCancel.cancelledTitle') }}</h1>
            <p class="text-gray-600 mb-2">{{ t('paymentCancel.cancelledMessage') }}</p>
            <p class="text-sm text-gray-500 mb-6">{{ t('paymentCancel.canRetry') }}</p>
          </div>

          <!-- Payment Details -->
          <div class="border-t border-gray-200 pt-6 mt-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('paymentCancel.detailsTitle') }}</h2>

            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">{{ t('paymentCancel.amount') }}</span>
                <span class="text-sm font-medium text-gray-900">{{ formattedAmount }}</span>
              </div>

              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">{{ t('paymentCancel.email') }}</span>
                <span class="text-sm font-medium text-gray-900">{{ paymentOnline.payer_email }}</span>
              </div>

              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">{{ t('paymentCancel.payer') }}</span>
                <span class="text-sm font-medium text-gray-900">{{ paymentOnline.payer_name }}</span>
              </div>
            </div>
          </div>

          <!-- Info Box -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div class="flex gap-3">
              <svg class="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div class="flex-1">
                <p class="text-sm text-blue-900 font-medium mb-1">{{ t('paymentCancel.whatNextTitle') }}</p>
                <p class="text-sm text-blue-800">
                  {{ t('paymentCancel.whatNextMessage') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center justify-center gap-3 pt-6 mt-6 border-t border-gray-200">
            <button
              @click="goToClub"
              class="px-6 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
            >
              {{ t('paymentCancel.backToClub') }}
            </button>
            <button
              @click="tryAgain"
              class="px-6 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700"
            >
              {{ t('paymentCancel.tryAgain') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
